/**
 * Shared threshold constants for data consistency across all modules
 *
 * IMPORTANT: All modules should use these constants instead of hardcoded values
 * to ensure consistent risk assessment, severity levels, and status displays.
 */

// =============================================================================
// HEALTH SCORE THRESHOLDS
// =============================================================================

export const HEALTH_THRESHOLDS = {
  CRITICAL: 50,      // Below this = critical condition
  WARNING: 70,       // Below this = warning condition
  GOOD: 85,          // Above this = good condition
  EXCELLENT: 95,     // Above this = excellent condition
} as const

export const HEALTH_STATUS = {
  CRITICAL: 'critical',
  WARNING: 'warning',
  GOOD: 'good',
  EXCELLENT: 'excellent',
} as const

export function getHealthStatus(healthScore: number): string {
  if (healthScore < HEALTH_THRESHOLDS.CRITICAL) return HEALTH_STATUS.CRITICAL
  if (healthScore < HEALTH_THRESHOLDS.WARNING) return HEALTH_STATUS.WARNING
  if (healthScore >= HEALTH_THRESHOLDS.EXCELLENT) return HEALTH_STATUS.EXCELLENT
  return HEALTH_STATUS.GOOD
}

export function getHealthLabel(healthScore: number): string {
  if (healthScore < HEALTH_THRESHOLDS.CRITICAL) return 'Critical'
  if (healthScore < HEALTH_THRESHOLDS.WARNING) return 'Poor'
  if (healthScore < HEALTH_THRESHOLDS.GOOD) return 'Fair'
  if (healthScore < HEALTH_THRESHOLDS.EXCELLENT) return 'Good'
  return 'Excellent'
}

// =============================================================================
// ANOMALY SCORE THRESHOLDS
// =============================================================================

export const ANOMALY_THRESHOLDS = {
  MINIMAL: 0.1,      // Below this = minimal anomaly
  LOW: 0.2,          // Below this = low anomaly
  MODERATE: 0.4,     // Below this = moderate anomaly
  HIGH: 0.6,         // Below this = high anomaly
  VERY_HIGH: 0.8,    // Above this = very high anomaly
} as const

export function getAnomalySeverity(anomalyScore: number): string {
  if (anomalyScore < ANOMALY_THRESHOLDS.MINIMAL) return 'minimal'
  if (anomalyScore < ANOMALY_THRESHOLDS.LOW) return 'low'
  if (anomalyScore < ANOMALY_THRESHOLDS.MODERATE) return 'moderate'
  if (anomalyScore < ANOMALY_THRESHOLDS.HIGH) return 'high'
  return 'very_high'
}

export function getAnomalyStatus(anomalyScore: number): 'good' | 'warning' | 'critical' {
  if (anomalyScore < ANOMALY_THRESHOLDS.MODERATE) return 'good'
  if (anomalyScore < ANOMALY_THRESHOLDS.HIGH) return 'warning'
  return 'critical'
}

// =============================================================================
// FAILURE TIME THRESHOLDS (in hours)
// =============================================================================

export const FAILURE_TIME_THRESHOLDS = {
  IMMINENT: 24,      // Less than 24 hours = imminent failure
  URGENT: 48,        // Less than 48 hours = urgent
  WARNING: 72,       // Less than 72 hours = warning
  MONITOR: 168,      // Less than 1 week = monitor closely
} as const

export function getFailureUrgency(hoursToFailure: number): string {
  if (hoursToFailure <= FAILURE_TIME_THRESHOLDS.IMMINENT) return 'IMMINENT'
  if (hoursToFailure <= FAILURE_TIME_THRESHOLDS.URGENT) return 'URGENT'
  if (hoursToFailure <= FAILURE_TIME_THRESHOLDS.WARNING) return 'WARNING'
  if (hoursToFailure <= FAILURE_TIME_THRESHOLDS.MONITOR) return 'MONITOR'
  return 'STABLE'
}

export function getFailureUrgencyStatus(hoursToFailure: number): 'critical' | 'warning' | 'good' {
  if (hoursToFailure <= FAILURE_TIME_THRESHOLDS.URGENT) return 'critical'
  if (hoursToFailure <= FAILURE_TIME_THRESHOLDS.WARNING) return 'warning'
  return 'good'
}

// =============================================================================
// FAILURE RISK CALCULATION
// =============================================================================

export type FailureRiskLevel = 'MINIMAL' | 'LOW' | 'MODERATE' | 'HIGH' | 'VERY HIGH'

export interface FailureRiskInput {
  anomalyScore?: number
  healthScore?: number
  failureTimeHours?: number
  confidenceScore?: number
}

/**
 * Comprehensive failure risk calculation considering multiple factors
 * Priority order: Time to failure > Health score > Anomaly score > Confidence
 */
export function calculateFailureRisk(input: FailureRiskInput): FailureRiskLevel {
  const { anomalyScore, healthScore, failureTimeHours, confidenceScore } = input

  // Priority 1: Imminent time to failure overrides everything
  if (failureTimeHours !== undefined) {
    if (failureTimeHours <= FAILURE_TIME_THRESHOLDS.IMMINENT) return 'VERY HIGH'
    if (failureTimeHours <= FAILURE_TIME_THRESHOLDS.URGENT) return 'HIGH'
    if (failureTimeHours <= FAILURE_TIME_THRESHOLDS.WARNING) return 'MODERATE'
  }

  // Priority 2: Critical health score indicates high risk
  if (healthScore !== undefined) {
    if (healthScore < HEALTH_THRESHOLDS.CRITICAL) return 'VERY HIGH'
    if (healthScore < HEALTH_THRESHOLDS.WARNING - 5) return 'HIGH' // Below 65
  }

  // Priority 3: High anomaly score indicates elevated risk
  if (anomalyScore !== undefined) {
    if (anomalyScore >= ANOMALY_THRESHOLDS.VERY_HIGH) return 'VERY HIGH'
    if (anomalyScore >= ANOMALY_THRESHOLDS.HIGH) return 'HIGH'
    if (anomalyScore >= ANOMALY_THRESHOLDS.MODERATE) return 'MODERATE'
    if (anomalyScore >= ANOMALY_THRESHOLDS.LOW) return 'LOW'
  }

  // Priority 4: High confidence in prediction with other concerning factors
  if (confidenceScore !== undefined && confidenceScore >= 0.8) {
    if (healthScore !== undefined && healthScore < HEALTH_THRESHOLDS.GOOD) return 'MODERATE'
  }

  // All indicators are good
  if (healthScore !== undefined && healthScore >= HEALTH_THRESHOLDS.GOOD &&
      (anomalyScore === undefined || anomalyScore < ANOMALY_THRESHOLDS.MINIMAL)) {
    return 'MINIMAL'
  }

  return 'LOW'
}

export function getFailureRiskStatus(risk: FailureRiskLevel): 'critical' | 'warning' | 'good' {
  if (risk === 'VERY HIGH' || risk === 'HIGH') return 'critical'
  if (risk === 'MODERATE') return 'warning'
  return 'good'
}

// =============================================================================
// RISK SEVERITY THRESHOLDS (for Predictive Ops)
// =============================================================================

export const RISK_SEVERITY_THRESHOLDS = {
  // Probability thresholds
  PROBABILITY_HIGH: 0.7,
  PROBABILITY_MEDIUM: 0.4,

  // Impact thresholds (1-5 scale)
  IMPACT_HIGH: 4,
  IMPACT_MEDIUM: 2,

  // Combined risk score thresholds (probability * impact / 5)
  COMBINED_CRITICAL: 0.6,
  COMBINED_HIGH: 0.4,
  COMBINED_MEDIUM: 0.2,
} as const

export type RiskSeverity = 'critical' | 'high' | 'medium' | 'low'

/**
 * Calculate risk severity considering BOTH probability AND impact
 * Risk = Probability × (Impact / 5) - normalized to 0-1 scale
 */
export function calculateRiskSeverity(probability: number, impact: number): RiskSeverity {
  const normalizedImpact = impact / 5 // Convert 1-5 scale to 0-1
  const combinedRisk = probability * normalizedImpact

  if (combinedRisk >= RISK_SEVERITY_THRESHOLDS.COMBINED_CRITICAL) return 'critical'
  if (combinedRisk >= RISK_SEVERITY_THRESHOLDS.COMBINED_HIGH) return 'high'
  if (combinedRisk >= RISK_SEVERITY_THRESHOLDS.COMBINED_MEDIUM) return 'medium'
  return 'low'
}

export function getRiskSeverityLabel(severity: RiskSeverity): string {
  const labels: Record<RiskSeverity, string> = {
    critical: 'Critical',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
  }
  return labels[severity]
}

export function getRiskStatus(severity: RiskSeverity): 'critical' | 'warning' | 'good' {
  if (severity === 'critical' || severity === 'high') return 'critical'
  if (severity === 'medium') return 'warning'
  return 'good'
}

// =============================================================================
// CONFIDENCE THRESHOLDS
// =============================================================================

export const CONFIDENCE_THRESHOLDS = {
  LOW: 0.6,          // Below this = low confidence
  MEDIUM: 0.75,      // Below this = medium confidence
  HIGH: 0.85,        // Above this = high confidence
  VERY_HIGH: 0.95,   // Above this = very high confidence
} as const

export function getConfidenceLevel(confidence: number): string {
  if (confidence < CONFIDENCE_THRESHOLDS.LOW) return 'Low'
  if (confidence < CONFIDENCE_THRESHOLDS.MEDIUM) return 'Medium'
  if (confidence < CONFIDENCE_THRESHOLDS.HIGH) return 'Good'
  if (confidence < CONFIDENCE_THRESHOLDS.VERY_HIGH) return 'High'
  return 'Very High'
}

// =============================================================================
// MAINTENANCE PRIORITY THRESHOLDS
// =============================================================================

export const MAINTENANCE_PRIORITY = {
  CRITICAL_DAYS: 7,     // Maintenance due within this many days = critical
  HIGH_DAYS: 14,        // Maintenance due within this many days = high priority
  MEDIUM_DAYS: 30,      // Maintenance due within this many days = medium priority
} as const

export function getMaintenancePriority(daysUntilDue: number): string {
  if (daysUntilDue < 0) return 'OVERDUE'
  if (daysUntilDue <= MAINTENANCE_PRIORITY.CRITICAL_DAYS) return 'CRITICAL'
  if (daysUntilDue <= MAINTENANCE_PRIORITY.HIGH_DAYS) return 'HIGH'
  if (daysUntilDue <= MAINTENANCE_PRIORITY.MEDIUM_DAYS) return 'MEDIUM'
  return 'LOW'
}

// =============================================================================
// COST CALCULATION CONSTANTS
// These should ideally come from API/configuration, not be hardcoded
// =============================================================================

export const COST_DEFAULTS = {
  // Default hourly downtime cost if not provided by API
  DEFAULT_DOWNTIME_COST_PER_HOUR: 5000,

  // Maintenance cost multipliers (relative to base maintenance cost)
  REACTIVE_MAINTENANCE_MULTIPLIER: 3.5,
  EMERGENCY_MAINTENANCE_MULTIPLIER: 5.0,

  // Cost escalation per day of delay
  DELAY_COST_ESCALATION_PER_DAY: 0.1, // 10% increase per day
} as const

// =============================================================================
// TREND ANALYSIS CONSTANTS
// =============================================================================

export const TREND_THRESHOLDS = {
  // Minimum data points needed for reliable trend analysis
  MIN_DATA_POINTS_FOR_TREND: 7,

  // Change percentage thresholds
  SIGNIFICANT_IMPROVEMENT: 5,    // >5% improvement
  SIGNIFICANT_DECLINE: -5,       // >5% decline

  // Volatility thresholds
  HIGH_VOLATILITY: 15,           // Standard deviation > 15%
  MODERATE_VOLATILITY: 8,        // Standard deviation > 8%
} as const

export function getTrendDirection(changePercent: number): 'improving' | 'stable' | 'declining' {
  if (changePercent >= TREND_THRESHOLDS.SIGNIFICANT_IMPROVEMENT) return 'improving'
  if (changePercent <= TREND_THRESHOLDS.SIGNIFICANT_DECLINE) return 'declining'
  return 'stable'
}

// =============================================================================
// STATUS COLOR MAPPING
// =============================================================================

export const STATUS_COLORS = {
  critical: {
    bg: 'bg-red-500/20',
    text: 'text-red-400',
    border: 'border-red-500/50',
    dot: 'bg-red-500',
  },
  warning: {
    bg: 'bg-yellow-500/20',
    text: 'text-yellow-400',
    border: 'border-yellow-500/50',
    dot: 'bg-yellow-500',
  },
  good: {
    bg: 'bg-green-500/20',
    text: 'text-green-400',
    border: 'border-green-500/50',
    dot: 'bg-green-500',
  },
  info: {
    bg: 'bg-blue-500/20',
    text: 'text-blue-400',
    border: 'border-blue-500/50',
    dot: 'bg-blue-500',
  },
} as const

export type StatusType = keyof typeof STATUS_COLORS
